import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import beverageImageSrc0 from "../../assets/beverageV7/0a.jpg";
import beverageImageSrc0desktop from "../../assets/beverageV6/0d.jpg";
import beverageImageSrc1 from "../../assets/beverageV6/1.jpg";
import beverageImageSrc2 from "../../assets/beverageV6/2.jpg";
import beverageImageSrc3 from "../../assets/beverageV6/3.jpg";
import beverageImageSrc4 from "../../assets/beverageV6/4.jpg";
import beverageImageSrc5 from "../../assets/beverageV6/5.jpg";
import { BeveragesGallery } from "./BeveragesGallery";
import { BeveragesDialog } from "./BeveragesDialog";
import { BeveragesDetailsDialog } from "./BeveragesDetailsDialog";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { useAcid } from "../../hooks/useAcid";
import { SignInDialogUi, ConsentPopup } from "../../components";
import { BeveragesSuccessDialog } from "./BeveragesSuccessDialog";
import { buildStructuredBeverageDataFromCategoryDetails } from "../../utils/beveragePayload";
import {
  getStoredBeverageConsent,
  shouldResetBeverageStateForAcidChange,
} from "../../utils/beverageSession";
import { isBeverageDataValid } from "./utils";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";

import styles from "./Beverages.module.css";

const desktopImages = [
  beverageImageSrc0desktop,
  beverageImageSrc1,
  beverageImageSrc2,
  beverageImageSrc3,
  beverageImageSrc4,
  beverageImageSrc5,
];

const mobileImages = [
  beverageImageSrc0,
  beverageImageSrc1,
  beverageImageSrc2,
  beverageImageSrc3,
  beverageImageSrc4,
  beverageImageSrc5,
];

const images = desktopImages;

export const BeverageV6 = () => {
  const cid = "beverageV6";
  const location = useLocation();
  const [isCategoriesDialogShown, setIsCategoriesDialogShown] = useState(false);
  const [isCustomizeDialogShown, setIsCustomizeDialogShown] = useState(false);
  const [isSignInDialogShown, setIsSignInDialogShown] = useState(false);
  const [isSuccessDialogShown, setIsSuccessDialogShown] = useState(false);
  const [isConsentDialogShown, setIsConsentDialogShown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesDetails, setCategoriesDetails] = useState({});
  const { acid } = useAcid();
  const [isSaving, setIsSaving] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  // Use a ref-based lock to block duplicate submissions immediately, before React's async state update takes effect.
  const savingRef = useRef(false);
  const previousAcidRef = useRef(acid);
  const currentAcidRef = useRef(acid);

  const resetBeverageStateForAcidChange = useCallback(() => {
    setIsCategoriesDialogShown(false);
    setIsCustomizeDialogShown(false);
    setIsSignInDialogShown(false);
    setIsSuccessDialogShown(false);
    setIsConsentDialogShown(false);
    setCategories([]);
    setCategoriesDetails({});
    setIsSaving(false);
    setIsRedirecting(false);
    savingRef.current = false;
  }, []);

  useEffect(() => {
    currentAcidRef.current = acid;
    if (shouldResetBeverageStateForAcidChange(previousAcidRef.current, acid)) {
      resetBeverageStateForAcidChange();
    }
    previousAcidRef.current = acid;
  }, [acid, resetBeverageStateForAcidChange]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => {
        const next = prev + 1;
        if (next >= images.length) {
          return 0;
        }
        return next;
      });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    return sendPageViewWhenConsentReady(
      "Beverage V6 Page",
      "Passions-FavBevs",
      "beverages6",
    );
  }, []);

  // Send analytics event when categories dialog opens
  useEffect(() => {
    if (isCategoriesDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V6 Modal - Categories",
        "Passions-FavBevs",
        "beverages6",
      );
    }
  }, [isCategoriesDialogShown]);

  // Send analytics event when customize dialog opens
  useEffect(() => {
    if (isCustomizeDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V6 Modal - Customize",
        "Passions-FavBevs",
        "beverages6",
      );
    }
  }, [isCustomizeDialogShown]);

  useEffect(() => {
    if (isSignInDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V6 Page Modal: Sign In",
        "Passions-FavBevs",
        "beverages6",
      );
    }
  }, [isSignInDialogShown]);

  useEffect(() => {
    if (isConsentDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V6 Page Modal: Consent",
        "Passions-FavBevs",
        "beverages6",
      );
    }
  }, [isConsentDialogShown]);

  useEffect(() => {
    if (isSuccessDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V6 Page Modal: Success",
        "Passions-FavBevs",
        "beverages6",
      );
    }
  }, [isSuccessDialogShown]);

  const onSubmitLoginDialog = () => {
    try {
      sessionStorage.setItem(
        "pendingBeverageV6Selections",
        JSON.stringify({
          categories,
          categoriesDetails,
          /**
           * Lets the acid-change guard in beverageSession.js recognize this
           * as this exact redirect's own in-flight data rather than
           * leftover cross-guest state, so it isn't wiped before this page
           * restores it.
           */
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("❌ Failed to save beverage selections:", error);
    }
  };

  const isValid = useMemo(() => {
    return isBeverageDataValid(categoriesDetails);
  }, [categoriesDetails]);

  const save = useCallback(
    async (overrideOptedOut) => {
      if (!acid) {
        setIsSignInDialogShown(true);
        return false;
      }

      const storedConsent = getStoredBeverageConsent();

      /**
       * Show consent popup if user hasn't responded for the current acid.
       * An explicit overrideOptedOut means agree/disagree are answering
       * consent right now — trust it instead of requiring sessionStorage to
       * already reflect an answer that hasn't been persisted yet.
       */
      if (overrideOptedOut === undefined && !storedConsent.hasResponded) {
        setIsConsentDialogShown(true);
        return false;
      }

      if (savingRef.current) {
        return false;
      }
      savingRef.current = true;

      try {
        const requestAcid = acid;
        setIsSaving(true);

        const data = buildStructuredBeverageDataFromCategoryDetails(
          categories,
          categoriesDetails,
        );
        // Pass the updated consent value directly to avoid using stale state before React applies the async update.
        const optedOut =
          overrideOptedOut !== undefined
            ? overrideOptedOut
            : storedConsent.optedOut !== null
              ? storedConsent.optedOut
              : false;
        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          {
            acid: requestAcid,
            structuredBeverageData: data,
            optedOutPersonalization: optedOut,
          },
        );
        if (currentAcidRef.current !== requestAcid) {
          return false;
        }

        setIsSuccessDialogShown(true);
        return true;
      } catch (err) {
        console.error("Error saving beverage selection:", err);
        return false;
      } finally {
        setIsSaving(false);
        savingRef.current = false;
      }
    },
    [acid, categories, categoriesDetails],
  );

  /**
   * sessionStorage is only written after save() actually succeeds, so a
   * failed request can't leave the session believing consent was recorded
   * (and therefore never prompting again) when CDP never received it.
   */
  const agree = useCallback(async () => {
    const saved = await save(false);
    if (saved) {
      sessionStorage.setItem("beverageConsentResponded", "true");
      sessionStorage.setItem("beverageConsentOptedOut", "false");
      setIsConsentDialogShown(false);
    }
  }, [save]);

  const disagree = useCallback(async () => {
    const saved = await save(true);
    if (saved) {
      sessionStorage.setItem("beverageConsentResponded", "true");
      sessionStorage.setItem("beverageConsentOptedOut", "true");
      setIsConsentDialogShown(false);
    }
  }, [save]);

  useEffect(() => {
    setCategoriesDetails((prev) => {
      const newDetails = {};
      categories.forEach((category) => {
        newDetails[category] = prev[category] ?? {
          timeOfDay: [],
          additionalDetails: "",
          types: [],
        };
      });
      return newDetails;
    });
  }, [categories]);

  useEffect(() => {
    try {
      const strData = sessionStorage.getItem("pendingBeverageV6Selections");
      const parsed = JSON.parse(strData);
      setCategories(parsed?.categories ?? []);
      setCategoriesDetails(parsed?.categoriesDetails ?? {});
      sessionStorage.removeItem("pendingBeverageV6Selections");
    } catch (err) {
      console.error(err);
    }
  }, []);

  const onSuccessDialogClose = () => {
    setIsRedirecting(true);
    setTimeout(() => {
      window.location.href = "https://www.marriott.com/default.mi";
    }, 1000);
  };

  return (
    <main className={styles.main}>
      <div className={styles.banner}>
        {desktopImages.map((image, index) => (
          <picture key={index}>
            <source media="(max-width: 768px)" srcSet={mobileImages[index]} />
            <img
              src={image}
              className={activeImageIndex === index ? styles.active : null}
            />
          </picture>
        ))}
        <div className={styles.bannerText}>
          <h1>
            What beverages do you prefer?
            <br />
            Help us get to know you better.
          </h1>

          <p>
            From morning coffee to evening cocktails,
            <br />
            tell us what completes your day.
            <br />
            <br />
            Click the plus sign below to get started.
          </p>
        </div>
      </div>

      <div className={styles.galleryWrapper}>
        <BeveragesGallery
          categories={categories}
          onAddClick={() => setIsCategoriesDialogShown(true)}
        />

        <div className={styles.buttonWrapper}>
          <button
            disabled={!categories.length || !isValid || isSaving}
            onClick={() => save()}
            className={`${styles.primary} custom_click_track`}
            data-custom_click_track_value="Beverages|Save Preferences|internal"
          >
            {isSaving ? "Saving preferences..." : "Save preferences"}
          </button>
        </div>

        <p className={styles.privacyText}>
          Preferences can be updated at any time in my{" "}
          <a
            href="https://www.marriott.com/loyalty/myAccount/default.mi"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.privacyLink}
          >
            Bonvoy Account Profile
          </a>
          , and I can learn more about how my data is collected and used in the{" "}
          <a
            href="https://www.marriott.com/about/privacy.mi"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.privacyLink}
          >
            Global Privacy Statement
          </a>
          .
        </p>
      </div>

      {isCategoriesDialogShown && (
        <BeveragesDialog
          initiallySelectedCategories={categories}
          setCategories={setCategories}
          customize={() => setIsCustomizeDialogShown(true)}
          onClose={() => setIsCategoriesDialogShown(false)}
        />
      )}

      {isCustomizeDialogShown && (
        <BeveragesDetailsDialog
          categoriesDetails={categoriesDetails}
          setNewDetails={setCategoriesDetails}
          onClose={() => setIsCustomizeDialogShown(false)}
          onBack={() => {
            setIsCustomizeDialogShown(false);
            setIsCategoriesDialogShown(true);
          }}
        />
      )}

      {isSignInDialogShown && (
        <SignInDialogUi
          onClose={() => setIsSignInDialogShown(false)}
          onSubmit={onSubmitLoginDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage6${stripAcidFromSearch(location.search)}`}
        />
      )}

      {isSuccessDialogShown && (
        <BeveragesSuccessDialog
          isRedirecting={isRedirecting}
          onClose={onSuccessDialogClose}
        ></BeveragesSuccessDialog>
      )}

      {isConsentDialogShown && (
        <ConsentPopup
          onAccept={agree}
          onDecline={disagree}
          onClose={() => setIsConsentDialogShown(false)}
          loading={isSaving}
        />
      )}
    </main>
  );
};
